import React, {
  createContext,
  useReducer,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from "react";
import { debounce } from "lodash";
import breakpoints from "../../_theme/base/breakpoints";

// Get the keys and values from the breakpoints
const breakpointEntries = Object.entries(breakpoints.values).reverse();

// Initial state for screen size
const initialState = {
  width: window.innerWidth,
  height: window.innerHeight,
  breakpoint: "xs",
  isResizing: false,
};

// Reducer function to manage screen size state
const screenSizeReducer = (state, action) => {
  switch (action.type) {
    case "START_RESIZING":
      return { ...state, isResizing: true };
    case "STOP_RESIZING":
      return {
        ...state,
        width: action.payload.width,
        height: action.payload.height,
        isResizing: false,
      };

    case "UPDATE_BREAKPOINT": // Add case to update breakpoint
      return { ...state, breakpoint: action.payload };
    default:
      return state;
  }
};

// Create a context for screen size
const ScreenSizeContext = createContext();

const ScreenSizeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(screenSizeReducer, initialState);

  const lastWidthRef = useRef(window.innerWidth);

  useEffect(() => {
    const handleBreakpoint = debounce(() => {
      const currentWidth = window.innerWidth; // Get the current width after resize
      // Find the current breakpoint based on window width
      const currentBreakpoint = breakpointEntries.find(([key, value]) => {
        return currentWidth >= value; // Check if the width is greater than or equal to the breakpoint value
      })[0];

      dispatch({ type: "UPDATE_BREAKPOINT", payload: currentBreakpoint });
    }, 300);
    const handleResizeStart = () => {
      // Calculate the difference between the new width and the last recorded width
      const currentWidth = window.innerWidth;
      const widthDifference = Math.abs(currentWidth - lastWidthRef.current);

      // Only dispatch START_RESIZING if the width has changed by more than 10px
      if (widthDifference > 0) {
        dispatch({ type: "START_RESIZING" });
      }
    };

    const handleResizeStop = debounce(() => {
      dispatch({
        type: "STOP_RESIZING",
        payload: { width: window.innerWidth, height: window.innerHeight },
      });
      // Update the last known width after the resizing has stopped
      lastWidthRef.current = window.innerWidth;

      handleBreakpoint();
    }, 300); // Delay of 300ms for debouncing stop event
    handleBreakpoint();
    // Add event listeners for the window resize
    window.addEventListener("resize", handleResizeStart); // Detect start of resize
    window.addEventListener("resize", handleResizeStop); // Handle stop after debounce

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener("resize", handleResizeStart);
      window.removeEventListener("resize", handleResizeStop);
    };
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
    }),
    [state]
  );
  return (
    <ScreenSizeContext.Provider value={value}>
      {children}
    </ScreenSizeContext.Provider>
  );
};

// Custom hook to use screen size in other components
export const useScreenSize = () => {
  const context = useContext(ScreenSizeContext);
  if (!context) {
    throw new Error("useScreenSize must be used within a ScreenSizeProvider");
  }

  if (!context && process.env.NODE_ENV !== "production") {
    throw new Error(
      "[ScreenSize]: `useScreenSize` must be wrapped in a <ScreenSizeProvider/>"
    );
  }
  return context;
};

export default ScreenSizeProvider;
